// Cart totals breakdown renderer (theme-owned).
//
// Northbound owns pricing math and exposes a `landedBreakdown` JSON object
// via `northbound:cart-pricing-applied` + `window.NorthboundStorefront.getCartPricingResult()`.
// The theme reads that JSON and renders the breakdown rows here so customer-
// facing labels live in `locales/en.default.json`, not in the app's runtime.
//
// Shipping breakdown rows render inside a native `<details>` element whose
// `<summary>` is the Shipping Less Subsidy row itself. The browser owns the
// expand/collapse interaction; this script only caches `details.open` so the
// state survives Dawn's `.js-contents` re-render after a cart mutation.
//
// Tax is rendered as its own top-level row, NOT inside the shipping
// disclosure. Northbound keeps product and shipping taxes separate in the
// payload, but the cart card presents one combined GST row.

(function () {
  if (window.__arcticCartBreakdownReady) return;
  window.__arcticCartBreakdownReady = true;

  var ROOT_SELECTOR = '[data-northbound-cart] [data-northbound-cart-breakdown-root]';
  var DISCLOSURE_SELECTOR = '[data-northbound-cart] [data-cart-breakdown-disclosure]';
  var TAX_ROW_SELECTOR = '[data-northbound-cart] [data-northbound-cart-tax-row]';
  var PACKAGING_FEE_ROW_SELECTOR = '[data-northbound-cart] [data-northbound-cart-packaging-fee-row]';
  var initializedDisclosures = new WeakSet();
  var cachedBreakdownHtml = '';
  var cachedDisclosureOpen = false;
  var cachedPackagingFee = null;
  var cachedTax = null;
  var pendingCartMutation = false;

  function getStrings() {
    return window.cartBreakdownStrings || {};
  }

  function formatMoney(cents) {
    var normalized = toNonNegativeInteger(cents);
    var runtime = window.__northboundStorefrontRuntime;
    var helpers = runtime && runtime.helpers;
    if (helpers && typeof helpers.formatMoney === 'function') {
      return helpers.formatMoney(normalized);
    }
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(normalized);
    }
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: 'CAD',
    }).format(normalized / 100);
  }

  function toNonNegativeInteger(value) {
    var parsed = Math.round(Number(value));
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
  }

  function formatWeightKg(grams) {
    var kg = grams / 1000;
    var fixed = Number.isInteger(kg) ? String(kg) : kg.toFixed(1).replace(/\.0$/, '');
    return fixed + 'kg';
  }

  function interpolate(template, vars) {
    if (typeof template !== 'string') return '';
    return template.replace(/\{\{\s*(\w+)\s*\}\}/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(vars, key) ? vars[key] : match;
    });
  }

  function levelCodeToNumber(code) {
    var match = String(code || '').match(/(\d+)/);
    return match ? match[1] : '';
  }

  function buildRow(labelText, amountCents, options) {
    var row = document.createElement('p');
    row.className = 'northbound-cart-breakdown__row';
    if (options && options.strong) {
      row.className += ' northbound-cart-breakdown__row--strong';
    }

    var label = document.createElement('span');
    label.className = 'northbound-cart-breakdown__label';
    label.textContent = labelText;

    var amount = document.createElement('span');
    amount.className = 'northbound-cart-breakdown__amount';
    if (options && options.negative) {
      amount.className += ' northbound-cart-breakdown__amount--negative';
      amount.textContent = '−' + formatMoney(amountCents);
    } else {
      amount.textContent = formatMoney(amountCents);
    }

    row.appendChild(label);
    row.appendChild(amount);
    return row;
  }

  function buildSubsidyLevelRow(level) {
    var strings = getStrings();
    var levelNumber = levelCodeToNumber(level && level.code);
    var weightGrams = toNonNegativeInteger(level && level.subsidyWeightGrams);
    var labelText;
    if (weightGrams > 0) {
      labelText = interpolate(strings.nncLevelSubsidyWithWeight || '', {
        level: levelNumber,
        weight: formatWeightKg(weightGrams),
      });
    } else {
      labelText = interpolate(strings.nncLevelSubsidy || '', { level: levelNumber });
    }
    return buildRow(labelText, level && level.subsidyCents, { negative: true });
  }

  function buildNoteRow(text) {
    var note = document.createElement('p');
    note.className = 'northbound-cart-breakdown__note';
    note.textContent = text;
    return note;
  }

  function render(breakdown, options) {
    var root = document.querySelector(ROOT_SELECTOR);
    if (!root) return;

    var subsidyLevels = Array.isArray(breakdown && breakdown.subsidyLevels)
      ? breakdown.subsidyLevels
      : [];

    if (subsidyLevels.length === 0) {
      cachedBreakdownHtml = '';
      cachedDisclosureOpen = false;
      hide(root);
      return;
    }

    var strings = getStrings();
    var fragment = document.createDocumentFragment();

    if (options && options.minimumApplied) {
      fragment.appendChild(
        buildNoteRow(strings.minimumShippingApplied || 'Minimum shipping charge applied.')
      );
    }

    fragment.appendChild(
      buildRow(strings.shippingBeforeSubsidy || 'Shipping Cost Before Subsidy:', breakdown.shippingBeforeSubsidyCents)
    );

    subsidyLevels.forEach(function (level) {
      fragment.appendChild(buildSubsidyLevelRow(level));
    });

    if (subsidyLevels.length > 1) {
      fragment.appendChild(
        buildRow(strings.totalNncSubsidy || 'Total NNC Subsidy:', breakdown.subsidyCents, {
          negative: true,
          strong: true,
        })
      );
    }

    root.innerHTML = '';
    root.appendChild(fragment);
    root.hidden = false;
    root.removeAttribute('data-cart-breakdown-stale');

    cacheBreakdown(root);
  }

  function hide(root) {
    var target = root || document.querySelector(ROOT_SELECTOR);
    if (!target) return;
    target.innerHTML = '';
    target.hidden = true;
  }

  function renderFromPricingResult(result) {
    var root = document.querySelector(ROOT_SELECTOR);
    if (!root) return;
    var breakdown = result && result.landedBreakdown;
    if (!breakdown || typeof breakdown !== 'object') {
      hide(root);
      renderTaxRow(null);
      renderPackagingFeeRow(null);
      applyDisclosureState();
      return;
    }
    render(breakdown, { minimumApplied: Boolean(result && result.minimumApplied) });
    renderTaxRow(breakdown);
    renderPackagingFeeRow(breakdown);
    applyDisclosureState();
  }

  function renderTaxRow(breakdown) {
    var row = document.querySelector(TAX_ROW_SELECTOR);
    if (!row) return;
    var label = row.querySelector('[data-northbound-cart-tax-label]');
    var total = row.querySelector('[data-northbound-cart-tax-total]');
    if (!label || !total) return;

    var cents =
      toNonNegativeInteger(breakdown && breakdown.productTaxCents) +
      toNonNegativeInteger(breakdown && breakdown.shippingTaxCents);
    if (cents <= 0) {
      row.hidden = true;
      total.textContent = '';
      row.removeAttribute('data-cart-tax-stale');
      cachedTax = null;
      return;
    }

    var strings = getStrings();
    label.textContent = strings.taxLabel || 'Tax (GST)';
    total.textContent = formatMoney(cents);
    row.hidden = false;
    row.removeAttribute('data-cart-tax-stale');
    cachedTax = { label: label.textContent, total: total.textContent };
  }

  function restoreTaxDuringCartMutation() {
    if (!pendingCartMutation || !cachedTax) return;
    var cartHasLines = Boolean(document.querySelector('[data-northbound-cart-line]'));
    if (!cartHasLines) return;

    var row = document.querySelector(TAX_ROW_SELECTOR);
    if (!row) return;
    var label = row.querySelector('[data-northbound-cart-tax-label]');
    var total = row.querySelector('[data-northbound-cart-tax-total]');
    if (!label || !total) return;
    if (!row.hidden && String(total.textContent || '').trim()) return;

    label.textContent = cachedTax.label;
    total.textContent = cachedTax.total;
    row.hidden = false;
    row.setAttribute('data-cart-tax-stale', '');
  }

  function renderPackagingFeeRow(breakdown) {
    var row = document.querySelector(PACKAGING_FEE_ROW_SELECTOR);
    if (!row) return;
    var label = row.querySelector('[data-northbound-cart-packaging-fee-label]');
    var total = row.querySelector('[data-northbound-cart-packaging-fee-total]');
    if (!label || !total) return;

    var cents = toNonNegativeInteger(breakdown && breakdown.packagingFeeCents);
    if (cents <= 0) {
      row.hidden = true;
      total.textContent = '';
      row.removeAttribute('data-cart-packaging-fee-stale');
      cachedPackagingFee = null;
      return;
    }

    var strings = getStrings();
    var weightGrams = toNonNegativeInteger(breakdown && breakdown.packagingWeightGrams);
    var labelText;
    if (weightGrams > 0) {
      labelText = interpolate(strings.packagingFeeWithWeight || 'Packaging Fee ({{ weight }}):', {
        weight: formatWeightKg(weightGrams),
      });
    } else {
      labelText = strings.packagingFee || 'Packaging Fee:';
    }

    label.textContent = labelText;
    total.textContent = formatMoney(cents);
    row.hidden = false;
    row.removeAttribute('data-cart-packaging-fee-stale');
    cachedPackagingFee = { label: labelText, total: total.textContent };
  }

  function findTotalsCard(root) {
    return root && root.closest ? root.closest('.cart-totals-card') : null;
  }

  function bindDisclosure(disclosure) {
    if (!disclosure || initializedDisclosures.has(disclosure)) return;
    var summary = disclosure.querySelector('summary');
    initializedDisclosures.add(disclosure);

    if (summary) {
      summary.addEventListener('click', function (event) {
        if (!isDisclosureAvailable(disclosure)) {
          event.preventDefault();
          cachedDisclosureOpen = false;
          disclosure.open = false;
          updateDisclosureAvailability(disclosure);
        }
      });

      summary.addEventListener('keydown', function (event) {
        var key = event.key || event.code;
        if (
          (key === 'Enter' || key === ' ' || key === 'Space' || key === 'Spacebar') &&
          !isDisclosureAvailable(disclosure)
        ) {
          event.preventDefault();
          cachedDisclosureOpen = false;
          disclosure.open = false;
          updateDisclosureAvailability(disclosure);
        }
      });
    }

    disclosure.addEventListener('toggle', function () {
      if (!isDisclosureAvailable(disclosure)) {
        cachedDisclosureOpen = false;
        if (disclosure.open) disclosure.open = false;
        updateDisclosureAvailability(disclosure);
        return;
      }
      cachedDisclosureOpen = disclosure.open;
    });
  }

  function isDisclosureAvailable(disclosure) {
    var root = disclosure && disclosure.querySelector('[data-northbound-cart-breakdown-root]');
    return Boolean(root && !root.hidden && root.children.length > 0);
  }

  function updateDisclosureAvailability(disclosure) {
    var summary = disclosure && disclosure.querySelector('summary');
    if (!disclosure || !summary) return;

    if (isDisclosureAvailable(disclosure)) {
      disclosure.removeAttribute('data-cart-breakdown-disabled');
      summary.removeAttribute('aria-disabled');
      summary.removeAttribute('tabindex');
      return;
    }

    cachedDisclosureOpen = false;
    disclosure.open = false;
    disclosure.setAttribute('data-cart-breakdown-disabled', '');
    summary.setAttribute('aria-disabled', 'true');
    summary.setAttribute('tabindex', '-1');
  }

  function applyDisclosureState() {
    var disclosure = document.querySelector(DISCLOSURE_SELECTOR);
    if (!disclosure) return;
    bindDisclosure(disclosure);

    updateDisclosureAvailability(disclosure);
    if (!isDisclosureAvailable(disclosure)) return;

    // Restore the user's expand state after Dawn replaces `.js-contents`.
    // Setting `open` synchronously also fires `toggle`; guard against
    // overwriting a freshly-set cached value by reading the attribute path.
    if (cachedDisclosureOpen && !disclosure.open) {
      disclosure.open = true;
    }
  }

  function cacheBreakdown(root) {
    if (!root || root.hidden || !root.children.length) return;
    if (pendingCartMutation && root.hasAttribute('data-cart-breakdown-stale')) return;
    cachedBreakdownHtml = root.innerHTML;
  }

  function restoreBreakdownDuringCartMutation(root) {
    if (!pendingCartMutation || !cachedBreakdownHtml || !root) return;
    if (!root.hidden && root.children.length) return;

    var cartItems = root.closest('cart-items');
    if (cartItems && cartItems.classList.contains('is-empty')) return;

    root.innerHTML = cachedBreakdownHtml;
    root.hidden = false;
    root.setAttribute('data-cart-breakdown-stale', '');
  }

  function restorePackagingFeeDuringCartMutation(card) {
    if (!pendingCartMutation || !cachedPackagingFee || !card) return;

    var cartHasLines = Boolean(document.querySelector('[data-northbound-cart-line]'));
    if (!cartHasLines) return;

    var row = card.querySelector('[data-northbound-cart-packaging-fee-row]');
    var label = card.querySelector('[data-northbound-cart-packaging-fee-label]');
    var total = card.querySelector('[data-northbound-cart-packaging-fee-total]');
    if (!row || !label || !total) return;
    if (!row.hidden && String(total.textContent || '').trim()) return;

    label.textContent = cachedPackagingFee.label;
    total.textContent = cachedPackagingFee.total;
    row.hidden = false;
    row.setAttribute('data-cart-packaging-fee-stale', '');
  }

  function bindCartUpdateRestore() {
    if (
      typeof subscribe !== 'function' ||
      typeof PUB_SUB_EVENTS === 'undefined' ||
      !PUB_SUB_EVENTS.cartUpdate
    ) {
      return;
    }

    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      pendingCartMutation = true;
      // Two paint frames: first to catch Dawn's synchronous section replace,
      // second as a safety net for any slightly-delayed re-render before
      // Northbound's pricing event fires the fresh render.
      requestAnimationFrame(applyRestore);
      setTimeout(applyRestore, 250);
    });
  }

  function applyRestore() {
    var root = document.querySelector(ROOT_SELECTOR);
    if (!root) return;
    restoreBreakdownDuringCartMutation(root);
    applyDisclosureState();
    restorePackagingFeeDuringCartMutation(findTotalsCard(root));
    restoreTaxDuringCartMutation();
  }

  function whenReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  }

  whenReady(function () {
    bindCartUpdateRestore();
    applyDisclosureState();
    renderFromPricingResult(getLatestCartPricingResult());
  });

  function getLatestCartPricingResult() {
    var api = window.NorthboundStorefront;
    if (!api || typeof api.getCartPricingResult !== 'function') return null;
    try {
      return api.getCartPricingResult();
    } catch (err) {
      return null;
    }
  }

  window.addEventListener('northbound:cart-pricing-applied', function (event) {
    pendingCartMutation = false;
    var result = event && event.detail && event.detail.cartPricingResult;
    renderFromPricingResult(result);
    applyDisclosureState();
  });

  window.addEventListener('northbound:community-changed', function () {
    pendingCartMutation = false;
    cachedBreakdownHtml = '';
    cachedPackagingFee = null;
    cachedTax = null;
    hide();
    applyDisclosureState();
    renderTaxRow(null);
    renderPackagingFeeRow(null);
  });
})();
